/**
 * 業務フロー図のエッジ描画。作図規約が決めた経路をそのまま引く。
 *
 * 経路の計算は描画エンジン（業務フロー図なら `business-flow-diagram/edges.ts`）の純関数が済ませ、
 * ここへはパス文字列で届く。
 * コンポーネント側に座標計算を持たせないのは、React Flow がエッジへ渡す `sourceX` /
 * `targetX` が**ハンドルの位置**であって格子の座標ではないためである。ここで計算し直すと、
 * 同じ経路の決定が純関数側とコンポーネント側の 2 箇所に分かれ、片方だけ規約から外れる。
 *
 * @graph-node {Function}
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 業務フロー図のエッジを、作図規約が決めた直交パスと矢印つきで描く。
 * @graph-connects none
 */
import { BaseEdge, EdgeLabelRenderer, type EdgeProps } from '@xyflow/react';

import { LABEL_ZONE_WIDTH } from './user-story-flow-layout.js';

/** エッジ 1 本に載せる描画済みの経路とラベル位置。 */
export interface FlowGutterEdgeData extends Record<string, unknown> {
  /** 作図規約に沿って組み立てた SVG パス。 */
  path: string;
  /** 条件ラベル。無ければ null。 */
  label: string | null;
  /** ラベルを置く X。 */
  labelX: number;
  /** ラベルを置く Y。 */
  labelY: number;
  /** 分岐の枝の色。分岐していない線と PJ 差分の線では null。 */
  branchColor?: string | null;
  /** 線を指したときに出す語（業務フロー図の戻る線の「戻る」）。省けば出さない。 */
  title?: string;
}

/** `data` が経路つきの形かを実行時に確かめる。 @graph-connects none */
function isGutterEdgeData(data: unknown): data is FlowGutterEdgeData {
  return (
    typeof data === 'object' &&
    data !== null &&
    typeof (data as { path?: unknown }).path === 'string'
  );
}

/**
 * 作図規約に沿った直交エッジ。
 *
 * `data` が期待の形で来なかった場合は何も描かない。壊れた形で既定のベジエ曲線へ落とすと、
 * 図の中に規約外の線が 1 本だけ混ざり、規約が守られているかを目視で判定できなくなる。
 *
 * @graph-node {Function}
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 作図規約が決めた直交パスと矢印でエッジを描き、条件ラベルを水平区間へ置き、語が渡れば線を指したときに出す。
 * @graph-connects none
 */
export function FlowGutterEdge({ data, markerEnd, style }: EdgeProps) {
  if (!isGutterEdgeData(data)) return null;

  const edge = (
    <BaseEdge
      path={data.path}
      markerEnd={markerEnd}
      style={typeof data.branchColor === 'string' ? { ...style, stroke: data.branchColor } : style}
    />
  );

  return (
    <>
      {/* 語は線（と React Flow が重ねる太い当たり判定）を包む `<title>` にする。指すと出る。 */}
      {typeof data.title === 'string' ? (
        <g data-edge-title={data.title}>
          <title>{data.title}</title>
          {edge}
        </g>
      ) : (
        edge
      )}
      {data.label !== null && (
        <EdgeLabelRenderer>
          {/*
           * 幅はラベル帯に固定し、超える文字は折り返す。幅を決めないと、実データの条件文
           * （1 文・30 文字超）が横一列に伸びて隣の列のノードを覆う。ラベルどうしの重なりを
           * 下へ逃がす処理（作図規約の規則 6）は、描画エンジンが位置を決める側で済ませて届ける。
           */}
          <div
            className="pointer-events-none absolute rounded border-l-[3px] bg-[var(--color-bg-surface)] py-px pr-1 pl-1.5 text-xs leading-4 break-words"
            style={{
              maxWidth: LABEL_ZONE_WIDTH,
              /*
               * 枝の色を左端の縦棒と文字に載せる。ラベルは線から離れた帯に置くので、色を
               * 共有しないとどの枝の説明なのかが図の上で決まらない。分岐していない線は色を
               * 持たないので、そのときは既定の淡い色に戻す。
               */
              borderLeftColor: data.branchColor ?? 'var(--color-border)',
              color: data.branchColor ?? 'var(--color-text-muted)',
              transform: `translate(-50%, -50%) translate(${String(data.labelX)}px, ${String(data.labelY)}px)`,
            }}
          >
            {data.label}
          </div>
        </EdgeLabelRenderer>
      )}
    </>
  );
}

/** React Flow へ渡すエッジ型の登録。1 種類しか無いので図の全線が同じ規約を通る。 @graph-connects none */
export const FLOW_EDGE_TYPES = { gutter: FlowGutterEdge };

/** エッジ型の名前。描画エンジンが付ける `type` と登録側を 1 つの定数から引く。 @graph-connects none */
export const FLOW_EDGE_TYPE = 'gutter';
